import React, { useState } from 'react';
import { Shield, Smartphone, Copy, Check, AlertCircle, QrCode } from 'lucide-react';
import useSettingsStore from '../../../store/settingsStore';
import { toast } from 'react-hot-toast';
import { QRCodeCanvas } from 'qrcode.react';

const TwoFactorAuth = () => {
  const { settings, setupTwoFactor, verifyTwoFactor, disableTwoFactor, isLoading } = useSettingsStore();
  const [step, setStep] = useState('initial');
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);

  const handleSetup = async () => {
    try {
      const data = await setupTwoFactor();
      setSetupData(data);
      setStep('setup');
    } catch (error) {}
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    try {
      await verifyTwoFactor(verificationCode);
      setStep('backup');
      toast.success('2FA enabled successfully! Save your backup codes.');
    } catch (error) {}
  };

  const handleDisable = async () => {
    if (!backupCode) {
      toast.error('Please enter a backup code to disable 2FA');
      return;
    }

    if (window.confirm('Are you sure you want to disable two-factor authentication?')) {
      try {
        await disableTwoFactor(backupCode);
        setStep('initial');
        setSetupData(null);
        setVerificationCode('');
        setBackupCode('');
        toast.success('2FA disabled successfully');
      } catch (error) {}
    }
  };

  const copyToClipboard = (text, index = null) => {
    navigator.clipboard.writeText(text);
    if (index !== null) {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    toast.success('Copied to clipboard');
  };

  if (settings?.twoFactorEnabled) {
    return (
      <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-6 border border-primary-100/30">
        <div className="flex items-center mb-4 sm:mb-6">
          <div className="p-1.5 sm:p-2 bg-green-50 rounded-lg mr-2 sm:mr-3">
            <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">Two-Factor Authentication</h3>
            <p className="text-xs sm:text-sm text-green-600 font-medium">Enabled</p>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6 shadow-neomorph-inset">
          <p className="text-xs sm:text-sm text-green-800">
            Two-factor authentication is enabled on your account. This adds an extra layer of security.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Enter a backup code to disable 2FA
            </label>
            <input
              type="text"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 mb-3 text-sm"
              maxLength="9"
            />
            <button
              onClick={handleDisable}
              disabled={isLoading}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium rounded-lg shadow-md shadow-red-500/30 hover:shadow-lg transition-all disabled:opacity-50 text-sm"
            >
              {isLoading ? 'Disabling...' : 'Disable 2FA'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-neomorph p-4 sm:p-6 border border-primary-100/30">
      <div className="flex items-center mb-4 sm:mb-6">
        <div className="p-1.5 sm:p-2 bg-primary-50 rounded-lg mr-2 sm:mr-3">
          <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
        </div>
        <div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-800">Two-Factor Authentication</h3>
          <p className="text-xs sm:text-sm text-gray-500">Add an extra layer of security to your account</p>
        </div>
      </div>

      {step === 'initial' && (
        <div>
          <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
            Two-factor authentication adds an additional layer of security to your account by requiring
            more than just a password to sign in.
          </p>
          <button
            onClick={handleSetup}
            disabled={isLoading}
            className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-sm"
          >
            {isLoading ? 'Setting up...' : 'Set up Two-Factor Authentication'}
          </button>
        </div>
      )}

      {step === 'setup' && setupData && (
        <div className="space-y-4 sm:space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 shadow-neomorph-inset">
            <h4 className="font-medium text-blue-800 mb-1.5 sm:mb-2 flex items-center text-sm sm:text-base">
              <QrCode className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
              Setup Instructions
            </h4>
            <ol className="list-decimal list-inside space-y-1 sm:space-y-2 text-xs sm:text-sm text-blue-700">
              <li>Install an authenticator app like Google Authenticator or Authy</li>
              <li>Scan the QR code below with the app</li>
              <li>Enter the 6-digit code from the app to verify</li>
            </ol>
          </div>

          <div className="flex flex-col items-center justify-center p-4 sm:p-6 bg-gray-50 rounded-lg shadow-neomorph-inset">
            {setupData.qrCode ? (
              <img src={setupData.qrCode} alt="2FA QR Code" className="w-32 h-32 sm:w-48 sm:h-48" />
            ) : (
              <QRCodeCanvas value={setupData.otpauthUrl || setupData.secret} size={160} className="sm:size-48" />
            )}
            <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600">
              Can't scan? Use this secret key:
            </p>
            <div className="flex flex-col sm:flex-row items-center mt-2 w-full sm:w-auto gap-2">
              <code className="bg-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg shadow-neomorph-inset text-xs sm:text-sm font-mono break-all text-center w-full sm:w-auto">
                {setupData.secret}
              </code>
              <button
                onClick={() => copyToClipboard(setupData.secret)}
                className="p-1.5 sm:p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all w-full sm:w-auto flex items-center justify-center"
              >
                {copied ? <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500" /> : <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500" />}
                <span className="ml-1.5 text-xs sm:hidden">{copied ? 'Copied!' : 'Copy'}</span>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              Verification Code
            </label>
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 mb-3 text-sm"
              maxLength="6"
            />
            <button
              onClick={handleVerify}
              disabled={isLoading || verificationCode.length !== 6}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all disabled:opacity-50 text-sm"
            >
              {isLoading ? 'Verifying...' : 'Verify and Enable'}
            </button>
          </div>
        </div>
      )}

      {step === 'backup' && setupData && (
        <div className="space-y-4 sm:space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 shadow-neomorph-inset">
            <div className="flex items-start">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500 mr-1.5 sm:mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-yellow-800 mb-1 sm:mb-2 text-sm sm:text-base">Save Your Backup Codes</h4>
                <p className="text-xs sm:text-sm text-yellow-700 mb-3 sm:mb-4">
                  These backup codes can be used to access your account if you lose your phone.
                  Each code can only be used once. Store them in a safe place.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 mb-3 sm:mb-4">
              {setupData.backupCodes && setupData.backupCodes.map((code, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between bg-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg border border-yellow-200 font-mono text-[11px] sm:text-sm shadow-neomorph-inset"
                >
                  <span className="break-all">{code}</span>
                  <button
                    onClick={() => copyToClipboard(code, index)}
                    className="ml-1.5 sm:ml-2 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                  >
                    {copiedIndex === index ? (
                      <Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
                    ) : (
                      <Copy className="h-3 w-3 sm:h-4 sm:w-4" />
                    )}
                  </button>
                </div>
              ))}
            </div>
            
            <button
              onClick={() => copyToClipboard(setupData.backupCodes.join('\n'))}
              className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Copy all codes
            </button>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => {
                setStep('initial');
                setSetupData(null);
                setVerificationCode('');
              }}
              className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all text-sm"
            >
              I've Saved My Codes
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default TwoFactorAuth;